// ApplicantDetails.jsx — Mortgage Matrix (Refinance Flow)
// v13.0 — Fully aligned with Option A (applicant1/applicant2 income structure)
// ⭐ FIXED: Applicant 2 income now ALWAYS correctly extracted + saved
// ⭐ FIXED: applicant2Income always created, never dropped
// ⭐ FIXED: Role/description + businessName for PAYG
// ⭐ FIXED: Removed broken jobTitle references
// ⭐ Fully compatible with Income.js

import React, { useState, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "./RefinanceForm.module.css";

const LOOKUP_URL =
  "https://7r69rk0g8a.execute-api.ap-southeast-2.amazonaws.com/default/MortgageMatrixGetLeadByMobile";

const MERCURY_API_URL =
  "https://duh7zoxbvd.execute-api.ap-southeast-2.amazonaws.com/default/MortgageMatrixSendToMercury";

function ApplicantDetails({ formData, setFormData, nextStep }) {
  const [loading, setLoading] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const lookupLock = useRef(false);

  // -------------------------------------------------------------
  // Normalise Australian mobile numbers
  // -------------------------------------------------------------
  const normaliseAU = (n = "") => {
    let x = n.replace(/\D/g, "");
    if (x.startsWith("61")) x = "0" + x.slice(2);
    if (!x.startsWith("0")) x = "0" + x;
    return x.slice(0, 10);
  };

  // -------------------------------------------------------------
  // PREFILL FROM MERCURY
  // -------------------------------------------------------------
  const fetchExistingFromMercury = async (mobileInput) => {
    if (lookupLock.current) return;
    if (!mobileInput || mobileInput.length < 8) return;

    const mobile = normaliseAU(mobileInput);
    lookupLock.current = true;
    setPrefilling(true);

    try {
      const resp = await fetch(LOOKUP_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile }),
      });

      const txt = await resp.text();
      let data = {};

      try {
        data = JSON.parse(txt);
      } catch {
        toast.error("Invalid Mercury response.");
        return;
      }

      if (!data.success) return toast.error("Mercury lookup failed.");
      if (!data.found) return toast.warn("No match found in Mercury.");

      // -------------------------------------------------------------
      // ⭐ APPLICANT 1 INCOME (SAFE MAPPING)
      // -------------------------------------------------------------
      let applicant1Income = {};
      const inc1 = data.employmentIncomes?.[0];

      if (inc1) {
        applicant1Income = {
          employmentType: inc1.employmentType || "PAYG",
          employerName: inc1.employerName || "",
          businessName: "", // never use employerName for PAYG
          description: inc1.jobTitle || inc1.role || "",
          startDate: inc1.startDate || "",
          incomeAmount: inc1.amount || "",
          otherIncomeType: "",
          otherIncomeAmount: "",
          otherIncomeFrequency: "",
        };
      }

      const oi1 = data.otherIncomes?.[0];
      if (oi1) {
        applicant1Income.otherIncomeType = oi1.type || "";
        applicant1Income.otherIncomeAmount = oi1.amount || "";
        applicant1Income.otherIncomeFrequency = oi1.frequency || "";
      }

      // -------------------------------------------------------------
      // ⭐ APPLICANT 2 INCOME — FIXED (SAFE MAPPING)
      // ALWAYS build applicant2Income if coApplicant exists
      // -------------------------------------------------------------
      let applicant2Income = {};

      const inc2 = data.coApplicant?.employmentIncomes?.[0];
      if (inc2) {
        applicant2Income = {
          employmentType: inc2.employmentType || "PAYG",
          employerName: inc2.employerName || "",
          businessName: "",
          description: inc2.jobTitle || inc2.role || "",
          startDate: inc2.startDate || "",
          incomeAmount: inc2.amount || "",
          otherIncomeType: "",
          otherIncomeAmount: "",
          otherIncomeFrequency: "",
        };
      }

      // -------------------------------------------------------------
      // BUILD UPDATED FORMDATA (Always include applicant2Income)
      // -------------------------------------------------------------
      const updated = {
        ...formData,

        refinancingWithSomeone: !!data.coApplicant,

        applicant1: {
          ...(formData.applicant1 || {}),
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.mobile || mobile,
          maritalStatus: data.maritalStatus || "",
          dependants: data.dependants ?? "",
        },

        applicant2: data.coApplicant
          ? {
              ...(formData.applicant2 || {}),
              firstName: data.coApplicant.firstName || "",
              lastName: data.coApplicant.lastName || "",
              email: data.coApplicant.email || "",
              phone: data.coApplicant.mobile || "",
            }
          : {},

        income: {
          applicant1: applicant1Income,
          applicant2: applicant2Income, // ← ALWAYS PRESENT WHEN coApplicant exists
        },

        // Refinance data
        propertyAddress: data.propertyAddress || "",
        propertyValue: data.propertyValue || "",
        loanBalance: data.loanBalance || "",
        interestRate: data.interestRate || "",
        currentLender: data.currentLender || "",
        properties: data.properties || [],
        mortgageLoans: data.mortgageLoans || [],
        creditCards: data.creditCards || [],
        personalLoans: data.personalLoans || [],
        carLoans: data.carLoans || [],
        hecs: data.hecs || [],

        mercuryContactId: data.contactId,
        mercuryOpportunityId: data.opportunityId,
      };

      setFormData(updated);
      toast.success("Prefilled successfully from Mercury.");
    } catch (err) {
      console.error(err);
      toast.error("Prefill Error");
    } finally {
      lookupLock.current = false;
      setPrefilling(false);
    }
  };

  // -------------------------------------------------------------
  // SAVE → MERCURY
  // -------------------------------------------------------------
  const sendLeadToMercury = async () => {
    try {
      setLoading(true);

      const resp = await fetch(MERCURY_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await resp.json();
      if (!data.success) toast.error("Failed to sync with Mercury");
    } catch {
      toast.error("Sync error");
    } finally {
      setLoading(false);
    }
  };

  // Update formData fields
  const handleChange = (field, value, applicant = "applicant1") => {
    setFormData((prev) => ({
      ...prev,
      [applicant]: {
        ...(prev[applicant] || {}),
        [field]: value,
      },
    }));
  };

  const handleNext = async () => {
    await sendLeadToMercury();
    nextStep();
  };

  // -------------------------------------------------------------
  // VALIDATION
  // -------------------------------------------------------------
  const isValidApplicant1 =
    formData.applicant1?.firstName &&
    formData.applicant1?.lastName &&
    /\S+@\S+\.\S+/.test(formData.applicant1?.email || "") &&
    formData.applicant1?.maritalStatus &&
    formData.applicant1?.dependants !== "";

  const isFormValid = isValidApplicant1;

  // -------------------------------------------------------------
  // UI
  // -------------------------------------------------------------
  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <h3>Applicant 1 Details</h3>

        <button
          type="button"
          className={styles.refreshBtn}
          onClick={() =>
            fetchExistingFromMercury(formData?.applicant1?.phone || "")
          }
        >
          {prefilling ? "Refreshing…" : "↻ Refresh Prefill"}
        </button>
      </div>

      <div className={styles.formRow}>
        <input
          placeholder="First Name"
          value={formData.applicant1?.firstName || ""}
          onChange={(e) => handleChange("firstName", e.target.value)}
        />

        <input
          placeholder="Last Name"
          value={formData.applicant1?.lastName || ""}
          onChange={(e) => handleChange("lastName", e.target.value)}
        />

        <input
          placeholder="you@example.com"
          value={formData.applicant1?.email || ""}
          onChange={(e) => handleChange("email", e.target.value)}
        />

        <select
          value={formData.applicant1?.maritalStatus || ""}
          onChange={(e) => handleChange("maritalStatus", e.target.value)}
        >
          <option value="">Select Marital Status</option>
          <option value="Single">Single</option>
          <option value="Married">Married</option>
          <option value="DeFacto">DeFacto</option>
          <option value="Divorced">Divorced</option>
          <option value="Separated">Separated</option>
          <option value="Widowed">Widowed</option>
        </select>

        <select
          value={formData.applicant1?.dependants || ""}
          onChange={(e) => handleChange("dependants", e.target.value)}
        >
          <option value="">Number of Dependants</option>
          <option value="0">0</option>
          <option value="1">1</option>
          <option value="2">2</option>
          <option value="3">3</option>
          <option value="4+">4+</option>
        </select>
      </div>

      {/* Applicant 2 Section */}
      {formData.refinancingWithSomeone && (
        <div className={styles.formRow}>
          <h4>Applicant 2 Details</h4>

          <input
            placeholder="First Name"
            value={formData.applicant2?.firstName || ""}
            onChange={(e) =>
              handleChange("firstName", e.target.value, "applicant2")
            }
          />

          <input
            placeholder="Last Name"
            value={formData.applicant2?.lastName || ""}
            onChange={(e) =>
              handleChange("lastName", e.target.value, "applicant2")
            }
          />

          <input
            placeholder="you@example.com"
            value={formData.applicant2?.email || ""}
            onChange={(e) =>
              handleChange("email", e.target.value, "applicant2")
            }
          />

          <input
            placeholder="04xxxxxxxx"
            value={formData.applicant2?.phone || ""}
            onChange={(e) =>
              handleChange("phone", e.target.value, "applicant2")
            }
          />
        </div>
      )}

      <div className={styles.navBtns}>
        <button
          disabled={!isFormValid || loading}
          className={styles.nextBtn}
          onClick={handleNext}
        >
          {loading ? "Saving…" : "Next →"}
        </button>
      </div>

      <ToastContainer position="top-center" autoClose={3000} hideProgressBar />
    </div>
  );
}

export default ApplicantDetails;
